<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { PerformanceView } from '~/features/my-wallet/types/receiving-model'
import { SolButton } from '@solfacil/girassol'
import { I18nT, useI18n } from 'vue-i18n'
import PerformanceGauge from '~/features/my-wallet/components/charts/PerformanceGauge.vue'
import { PERFORMANCE_WINDOW_DAYS, PROJECTS_INSTALLATION_QUERY, PROJECTS_ROUTE, SLA_ON_TIME_DAYS } from '~/features/my-wallet/constants/my-wallet'

interface Props {
  performance: PerformanceView
}

const props = defineProps<Props>()

const { t } = useI18n()
const router = useRouter()

type CompletionTab = 'on_time' | 'late'

const COMPLETION_TABS: CompletionTab[] = ['on_time', 'late']
const activeTab = ref<CompletionTab>('on_time')

const isOnTime = computed(() => activeTab.value === 'on_time')
const activeTabCount = computed(() => isOnTime.value ? props.performance.onTimeCompleted : props.performance.lateCompleted)
const gaugeAria = computed(() => t(`my_wallet.receiving_model.completion.gauge_aria.${activeTab.value}`, {
  count: activeTabCount.value,
  total: props.performance.totalCompleted,
}))

function tabId(tab: CompletionTab) {
  return `my-wallet-completion-tab-${tab}`
}

const tabButtons = new Map<CompletionTab, HTMLButtonElement>()

function setTabButton(tab: CompletionTab, element: Element | ComponentPublicInstance | null) {
  if (element instanceof HTMLButtonElement)
    tabButtons.set(tab, element)
  else
    tabButtons.delete(tab)
}

function handleTabKeydown(event: KeyboardEvent, index: number) {
  const targetIndexByKey: Record<string, number> = {
    ArrowRight: (index + 1) % COMPLETION_TABS.length,
    ArrowLeft: (index - 1 + COMPLETION_TABS.length) % COMPLETION_TABS.length,
    Home: 0,
    End: COMPLETION_TABS.length - 1,
  }
  const targetIndex = targetIndexByKey[event.key]
  if (targetIndex === undefined)
    return

  event.preventDefault()
  activeTab.value = COMPLETION_TABS[targetIndex]
  tabButtons.get(COMPLETION_TABS[targetIndex])?.focus()
}

function goToProjects() {
  router.push({ path: PROJECTS_ROUTE, query: PROJECTS_INSTALLATION_QUERY })
}
</script>

<template>
  <div class="completion-breakdown">
    <div class="completion-breakdown-tabs" role="tablist" :aria-label="t('my_wallet.receiving_model.completion.tabs_aria')">
      <button
        v-for="(tab, index) in COMPLETION_TABS"
        :id="tabId(tab)"
        :key="tab"
        :ref="element => setTabButton(tab, element)"
        type="button"
        role="tab"
        class="completion-breakdown-tab"
        :class="{ '-active': activeTab === tab }"
        :aria-selected="activeTab === tab"
        :tabindex="activeTab === tab ? 0 : -1"
        aria-controls="my-wallet-completion-panel"
        @click="activeTab = tab"
        @keydown="handleTabKeydown($event, index)"
      >
        {{ t(`my_wallet.receiving_model.completion.tabs.${tab}`) }}
      </button>
      <span class="completion-breakdown-tabs-fill" aria-hidden="true" />
    </div>

    <div
      id="my-wallet-completion-panel"
      class="completion-breakdown-panel"
      role="tabpanel"
      :aria-labelledby="tabId(activeTab)"
    >
      <div class="completion-breakdown-heading">
        <h4 class="completion-breakdown-title">
          {{ t(`my_wallet.receiving_model.completion.${activeTab}.title`) }}
        </h4>
        <p class="completion-breakdown-description">
          {{ t(`my_wallet.receiving_model.completion.${activeTab}.description`) }}
        </p>
      </div>

      <div class="completion-breakdown-content">
        <div class="completion-breakdown-total">
          <PerformanceGauge
            :percentage="performance.percentage"
            :thresholds="performance.thresholds"
            :inverted="!isOnTime"
            :muted="performance.muted"
            :commercial-exception="performance.commercialException"
            :label="gaugeAria"
          />
          <div class="completion-breakdown-total-text">
            <p class="completion-breakdown-count">
              {{ t('my_wallet.receiving_model.completion.count', { count: activeTabCount, total: performance.totalCompleted }) }}
            </p>
            <p class="completion-breakdown-caption">
              <I18nT keypath="my_wallet.receiving_model.completion.caption" tag="span">
                <template #status>
                  <strong class="completion-breakdown-status" :class="`-${activeTab}`">
                    {{ t(`my_wallet.receiving_model.completion.${activeTab}.status`) }}
                  </strong>
                </template>
              </I18nT>
              <br>
              {{ t('my_wallet.receiving_model.completion.caption_window', { days: PERFORMANCE_WINDOW_DAYS }) }}
            </p>
          </div>
          <SolButton
            id="my-wallet-completion-see-projects"
            variant="secondary"
            size="small"
            @click="goToProjects"
          >
            {{ t('my_wallet.receiving_model.completion.see_projects') }}
          </SolButton>
        </div>

        <svg class="completion-breakdown-divider" viewBox="0 0 13 100" preserveAspectRatio="none" aria-hidden="true">
          <path d="M3 0 L3 40 L11 50 L3 60 L3 100" fill="none" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke" />
        </svg>

        <div
          v-for="kit in performance.kits"
          :key="kit.kitType"
          class="completion-breakdown-kit"
        >
          <span class="completion-breakdown-kit-tag" :class="`-${activeTab}`">
            <img
              v-if="kit.kitType === 'SOLFACIL_COMBO'"
              src="/images/minha-carteira/solfacil-mark.svg"
              alt=""
              class="completion-breakdown-kit-icon"
            >
            {{ t(`my_wallet.receiving_model.completion.kits.${kit.kitType}`) }}
          </span>
          <div class="completion-breakdown-kit-text">
            <p class="completion-breakdown-count">
              {{ t('my_wallet.receiving_model.completion.kit_count', { count: isOnTime ? kit.onTimeCount : kit.lateCount }, isOnTime ? kit.onTimeCount : kit.lateCount) }}
            </p>
            <p class="completion-breakdown-caption">
              {{ t(`my_wallet.receiving_model.completion.${activeTab}.kit_prefix`) }}
            </p>
            <p class="completion-breakdown-sla" :class="`-${activeTab}`">
              {{ t(`my_wallet.receiving_model.completion.${activeTab}.sla`, { days: kit.slaDays }) }}
              <br>
              {{ t(`my_wallet.receiving_model.completion.counted_from.${kit.kitType}`) }}
            </p>
          </div>
        </div>
      </div>

      <div class="completion-breakdown-banner">
        <img src="/images/minha-carteira/solfacil-mark.svg" alt="" class="completion-breakdown-banner-icon">
        <div class="completion-breakdown-banner-text">
          <p class="completion-breakdown-banner-title">
            {{ t('my_wallet.receiving_model.completion.combo_banner.title') }}
          </p>
          <I18nT keypath="my_wallet.receiving_model.completion.combo_banner.text" tag="p">
            <template #highlight>
              <span class="completion-breakdown-underline">
                {{ t('my_wallet.receiving_model.completion.combo_banner.highlight', { days: SLA_ON_TIME_DAYS.SOLFACIL_COMBO }) }}
              </span>
            </template>
          </I18nT>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.completion-breakdown {
  @apply flex flex-col w-full;

  .completion-breakdown-tabs {
    @apply flex items-end;
  }

  .completion-breakdown-tab {
    @apply w-1/2 h-[58px] px-3 rounded-t-lg cursor-pointer;
    @apply fonts-body-medium-bold text-neutral-low-dark bg-neutral-high-light;
    @apply border-t border-l border-r border-transparent;
    margin-bottom: -1px;

    @screen sm:system {
      @apply w-[188px] px-4 fonts-body-large-bold whitespace-nowrap;
    }

    &.-active {
      @apply relative z-10 bg-neutral-high-pure border-neutral-high-medium;
    }
  }

  .completion-breakdown-tabs-fill {
    @apply hidden flex-1;

    @screen sm:system {
      @apply block;
    }
  }

  .completion-breakdown-panel {
    @apply flex flex-col px-4 pt-6 pb-6 rounded-b-lg;
    @apply bg-neutral-high-pure border border-neutral-high-medium;

    @screen sm:system {
      @apply px-6 rounded-tr-lg;
    }
  }

  .completion-breakdown-heading {
    @apply flex flex-col gap-2 text-neutral-low-dark;
  }

  .completion-breakdown-title {
    @include typography.title-20;
  }

  .completion-breakdown-description {
    @apply fonts-body-medium-regular;
  }

  .completion-breakdown-content {
    @apply flex flex-col gap-6 py-6;

    @screen md:system {
      @apply flex-row items-center justify-between gap-0 px-4 py-8;
    }
  }

  .completion-breakdown-total,
  .completion-breakdown-kit {
    @apply flex flex-col items-center flex-1 min-w-0 text-center;

    @screen md:system {
      @apply px-8;
    }
  }

  .completion-breakdown-total {
    @apply gap-6;
  }

  .completion-breakdown-kit {
    @apply gap-4;
  }

  .completion-breakdown-total-text,
  .completion-breakdown-kit-text {
    @apply flex flex-col items-center gap-2;
  }

  .completion-breakdown-count {
    @apply text-neutral-low-dark;
    @include typography.title-20(1.5);
  }

  .completion-breakdown-caption {
    @apply fonts-body-medium-regular text-brand-primary-medium;
    line-height: 1.5;
  }

  .completion-breakdown-status {
    @apply fonts-body-medium-bold;

    &.-on_time {
      @apply text-brand-secondary-dark;
    }

    &.-late {
      @apply text-feedback-warning-dark;
    }
  }

  .completion-breakdown-divider {
    @apply hidden self-stretch flex-shrink-0 text-neutral-high-medium;
    width: 13px;

    @screen md:system {
      @apply block;
    }
  }

  .completion-breakdown-kit-tag {
    @apply inline-flex items-center gap-1 h-6 px-3 rounded-full whitespace-nowrap;
    @apply fonts-body-small-bold;

    &.-on_time {
      @apply bg-feedback-positive-light text-feedback-positive-dark;
    }

    &.-late {
      @apply bg-neutral-high-light text-neutral-low-medium;
    }
  }

  .completion-breakdown-kit-icon {
    @apply w-4 h-4;
  }

  .completion-breakdown-sla {
    @apply fonts-body-large-bold;
    line-height: 1.375;

    &.-on_time {
      @apply text-brand-secondary-dark;
    }

    &.-late {
      @apply text-feedback-warning-dark;
    }
  }

  .completion-breakdown-banner {
    @apply flex items-center gap-2 px-4 pt-3 pb-4 rounded-md;
    @apply border border-brand-secondary-medium;
    @apply bg-brand-secondary-light;
  }

  .completion-breakdown-banner-icon {
    @apply w-6 h-6 flex-shrink-0;
  }

  .completion-breakdown-banner-text {
    @apply flex flex-col gap-1 min-w-0 fonts-body-medium-regular text-neutral-low-dark;
  }

  .completion-breakdown-banner-title {
    @apply fonts-body-medium-bold;
  }

  .completion-breakdown-underline {
    @apply underline;
  }
}
</style>
